import { useCallback, useEffect, type ReactNode } from 'react';

import { useBackHandler } from '@/lib/backButton';
import { useIsMobile } from '@/hooks/useIsMobile';
import { cn } from '@/lib/utils';

export type ModalProps = {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /**
   * Окно сотрудника на телефоне — во весь экран (docs/CURATOR-MOBILE-DESIGN.md §3, правило 2): шапка с крестиком
   * 44 px, середина прокручивается, кнопки `footer` прибиты к низу над полосой жеста. На широком экране и без этого
   * признака окно прежнее — ученические окна им не пользуются.
   */
  phoneFull?: boolean;
  /** Полоса кнопок под прокручиваемой серединой. Без неё кнопки остаются в `children`, как раньше. */
  footer?: ReactNode;
};

/**
 * Modal — uses the brand's glass surface for a "Level Up" feel on overlays.
 * Closes on Esc or backdrop click.
 *
 * NOTE: we render with a plain backdrop+panel rather than pulling Radix to keep
 * dependency footprint tiny. If we need focus-trap / scroll-lock at scale,
 * swap for @radix-ui/react-dialog later.
 */
export function Modal({ open, onClose, title, children, size = 'md', phoneFull = false, footer }: ModalProps) {
  const isMobile = useIsMobile();
  const full = phoneFull && isMobile;
  // Аппаратная «Назад» на Android закрывает окно, а не всё приложение. Живёт
  // здесь, а не на 23 экранах с окнами: компонент один, поведение общее.
  const handleBack = useCallback(() => {
    onClose();
    return true;
  }, [onClose]);
  useBackHandler(handleBack, open);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', handler);
    // lock background scroll while modal is open
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handler);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  // Во весь экран: безопасные поля — внутри листа, а не вокруг него, иначе под часами и над полосой жеста
  // просвечивала бы затемнённая страница. Клавиатура в приложении сжимает экран сама (KeyboardResize.Native),
  // поэтому лист высотой h-full держит полосу кнопок над ней.
  // ⚠️ `margin: 0` СТИЛЕМ, А НЕ КЛАССОМ (13.10.2026, кадр 390 на стенде). Окно рисуется там, где его вызвали, — часто
  // внутри `space-y-*`, а Tailwind даёт каждому следующему ребёнку `margin-top`. У слоя `fixed inset-0` этот отступ
  // сдвигал всё окно вниз (лист «Истории правок» начинался на 10 px ниже верха, сверху просвечивала страница).
  // Класс `m-0` селектору `space-y` проигрывает по весу — поэтому стиль.
  if (full) {
    return (
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? 'modal-title' : undefined}
        className="fixed inset-0 z-50 flex"
        style={{ margin: 0 }}
      >
        <div
          data-modal-phone-full
          className="app-dialog relative flex h-full w-full flex-col overflow-hidden bg-surface-container-lowest pb-[var(--sa-bottom)] pl-[var(--sa-left)] pr-[var(--sa-right)] pt-[var(--sa-top)]"
        >
          <div className="flex shrink-0 items-center gap-2 border-b border-outline py-1.5 pl-4 pr-1.5">
            <h2 id="modal-title" className="min-w-0 flex-1 font-display text-[17px] font-bold leading-tight text-on-surface">
              {title}
            </h2>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="grid h-11 w-11 flex-none place-items-center rounded-full text-on-surface-variant"
            >
              <span className="material-symbols-outlined">close</span>
            </button>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-4 pt-3">{children}</div>
          {footer && <div className="shrink-0 border-t border-outline px-4 py-3">{footer}</div>}
        </div>
      </div>
    );
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? 'modal-title' : undefined}
      className="safe-inset fixed inset-0 z-50 flex items-end justify-center px-4 py-6 md:items-center"
      // Тот же сдвиг от `space-y-*` родителя, что у окна во весь экран (см. выше).
      style={{ margin: 0 }}
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-inverse-surface/40" aria-hidden />
      <div
        onClick={(e) => e.stopPropagation()}
        className={cn(
          // ⚠️ max-h-full + flex-колонка — то, из-за чего окна вообще
          // прокручиваются. Раньше стоял только overflow-hidden без предельной
          // высоты: содержимое длиннее экрана ОБРЕЗАЛОСЬ, прокручивать было
          // нечего, и кнопка сохранения оставалась за краем. Настройки курса
          // из-за этого нельзя было сохранить вообще (заказчик, 28.08.2026).
          //
          // overflow-hidden остаётся: он подрезает содержимое по скруглённым
          // углам. Прокрутка живёт во внутренней области ниже.
          // `app-dialog` — метка для песочного вида: см. index.css.
          'app-dialog relative flex max-h-full w-full flex-col overflow-hidden rounded-card bg-surface-container-lowest shadow-ambient',
          size === 'sm' && 'max-w-sm',
          size === 'md' && 'max-w-lg',
          size === 'lg' && 'max-w-2xl',
          size === 'xl' && 'max-w-4xl',
        )}
      >
        {title && (
          // shrink-0: заголовок остаётся на месте, когда содержимое
          // прокручивается под ним.
          <div className="flex shrink-0 items-center justify-between px-6 pt-6">
            <h2 id="modal-title" className="font-display text-xl font-bold tracking-display text-on-surface">
              {title}
            </h2>
            <button
              onClick={onClose}
              aria-label="Close"
              className="rounded-button p-1.5 text-on-surface-variant hover:bg-surface-container-low"
            >
              <span className="material-symbols-outlined">close</span>
            </button>
          </div>
        )}
        {/*
          Прокручивается ИМЕННО эта область, а не окно целиком: заголовок с
          крестиком обязан оставаться на виду в длинной форме.

          min-h-0 обязателен. Без него элемент внутри flex-колонки не сжимается
          меньше своего содержимого, предельная высота не срабатывает — и
          прокрутка не появляется, хотя overflow-y-auto стоит.
        */}
        <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-6 pt-4">{children}</div>
        {footer && <div className="shrink-0 px-6 pb-6">{footer}</div>}
      </div>
    </div>
  );
}
